import { useMemo } from 'react';
import useDebounce from '@/hooks/useDebounce';
import { useGetCommercesQuery } from '@/pages/Business/pages/Commerce/services/commerces.service';
import { Option } from '@/components/inputs/AutocompleteInput';

const DEBOUNCE_MS = 350;

interface UseTransitControlCommercesAutocompleteProps {
  search?: string;
  minLength?: number;
}

const useTransitControlCommercesAutocomplete = ({
  search,
  minLength = 3,
}: UseTransitControlCommercesAutocompleteProps = {}) => {
  const trimmedLive = (search ?? '').trim();
  const debouncedSearch = useDebounce(trimmedLive, DEBOUNCE_MS);
  const isSyncedWithInput = debouncedSearch === trimmedLive;
  const shouldSearch = isSyncedWithInput && debouncedSearch.length >= minLength;

  const {
    data: commercesData,
    isLoading: isCommercesLoading,
  } = useGetCommercesQuery(
    {
      paginated: { page: 1, pageSize: 1000 },
      filters: { name: shouldSearch ? debouncedSearch : '' },
    },
    { skip: !shouldSearch }
  );

  const commerceOptions: Option[] = useMemo(() => {
    if (isCommercesLoading || !commercesData?.items) return [];
    return commercesData.items.map((commerce) => ({
      label: commerce.name,
      data: commerce.id,
    }));
  }, [isCommercesLoading, commercesData?.items]);

  return { commerceOptions, isCommercesLoading };
};

export default useTransitControlCommercesAutocomplete;
